import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import { API_URL } from './config'

const Api = {
  init () {
    Vue.use(VueAxios, axios)
    Vue.axios.defaults.baseURL = API_URL
  },

  setAuth (username, password) {
    Vue.axios.defaults.auth = {
      username, password
    }
  },

  async checkLogin () {
    try {
      await LanguagesApi.query()

      return true
    } catch (e) {
      return false
    }
  },

  post (resource, params) {
    return Vue.axios.post(`${resource}`, params)
  },

  put (resource, params) {
    return Vue.axios.put(`${resource}/${params.id}`, params)
  },

  delete (resource, params) {
    return Vue.axios.delete(`${resource}/${params.id}`)
  },

  query (resource, params) {
    return Vue.axios.get(resource, params).catch(error => {
      throw new Error(`[App] ApiService ${error}`)
    })
  }
}

export default Api

export const DictionaryApi = {
  query (dictionary, params) {
    return Api.query(`dictionary/${dictionary}`, { params })
  },
  create (dictionary) {
    return Api.post(`dictionaries`, dictionary)
  },
  createWord (dictionary, word) {
    return Api.post(`dictionary/${dictionary}`, word)
  },
  updateWord (dictionary, word) {
    return Api.put(`dictionary/${dictionary}`, word)
  },
  alphabet (dictionary) {
    return Api.query(`dictionary/${dictionary.id}/letters`)
  }
}

export const WordsApi = {
  query (params) {
    return Api.query(`words`, { params })
  },
  duplicates (sourceDictionary, wordText) {
    return WordsApi.query({ sourceDictionaryId: sourceDictionary.id, word: wordText })
  },
  create (word) {
    return Api.post(`words`, word)
  },
  update (word) {
    return Api.put(`words`, word)
  },
  delete (word) {
    return Api.delete(`words`, word)
  },
  move (word, position) {
    return Vue.axios.put(`words/${word.id}/move`, { position })
  }
}

export const CategoriesApi = {
  query (dictionary) {
    return Api.query(`source-dictionaries/${dictionary}/categories`)
  },
  queryWithTranslations (dictionary) {
    return Api.query(`dictionary/${dictionary}/categories`)
  },
  create (category) {
    return Api.post('categories', category)
  },
  update (category) {
    return Api.put('categories', category)
  }
}

export const SectionsApi = {
  query (dictionary) {
    return Api.query(`source-dictionaries/${dictionary}/sections`)
  },
  queryWithTranslations (dictionary) {
    return Api.query(`dictionary/${dictionary}/sections`)
  },
  create (section) {
    return Api.post('sections', section)
  },
  update (section) {
    return Api.put('sections', section)
  }
}

export const TranslatedSectionsApi = {
  create (section, translatedSection) {
    return Api.post(`sections/${section.id}/translations`, translatedSection)
  },
  update (section, translatedSection) {
    return Api.put(`sections/${section.id}/translations`, translatedSection)
  }
}

export const TranslatedCategoriesApi = {
  create (category, translatedCategory) {
    return Api.post(`categories/${category.id}/translations`, translatedCategory)
  },
  update (category, translatedCategory) {
    return Api.put(`categories/${category.id}/translations`, translatedCategory)
  }
}

export const LanguagesApi = {
  query () {
    return Api.query(`languages`)
  },
  create (language) {
    return Api.post('languages', language)
  },
  update (language) {
    return Api.put('languages', language)
  }
}

export const SourceDictionariesApi = {
  query () {
    return Api.query(`source-dictionaries`)
  },
  create (sourceDictionary) {
    return Api.post('source-dictionaries', sourceDictionary)
  },
  update (sourceDictionary) {
    return Api.put('source-dictionaries', sourceDictionary)
  },
  alphabet (sourceDictionary) {
    return Api.query(`source-dictionaries/${sourceDictionary.id}/letters`)
  },
  baseWords (sourceDictionary, params) {
    return Api.query(`source-dictionaries/${sourceDictionary.id}/basewords`, { params })
  },
  ignoreBaseWord (sourceDictionary, baseword) {
    return Api.post(`source-dictionaries/${sourceDictionary.id}/basewords/${baseword.id}/ignore`)
  },
  importedCategories (sourceDictionary) {
    return Api.query(`source-dictionaries/${sourceDictionary.id}/imported-categories`)
  }
}

export const StorageApi = {
  copy (type, url, text, metadata) {
    return Api.post('storage/copy', { type, url, text, metadata })
  },
  upload (type, name, file, text) {
    // see https://github.com/axios/axios/blob/master/examples/upload/index.html
    const data = new FormData()
    data.append('type', type)
    data.append('name', name)
    data.append('file', file)
    data.append('text', text)

    return Api.post('storage/upload', data)
  }
}

export const TranslationApi = {
  create (word, translation) {
    return Api.post(`words/${word.id}/translations`, translation)
  },
  update (word, translation) {
    return Api.put(`words/${word.id}/translations`, translation)
  }
}

export const LettersApi = {
  create (letter) {
    return Api.post(`letters`, letter)
  },
  update (letter) {
    return Api.put(`letters`, letter)
  },
  move (letter, position) {
    return Vue.axios.put(`letters/${letter.id}/move`, { position })
  }
}

export const TransliteratedLetterApi = {
  create (letter, transliteratedLetter) {
    return Api.post(`letters/${letter.id}/transliterated`, transliteratedLetter)
  },
  update (letter, transliteratedLetter) {
    return Api.put(`letters/${letter.id}/transliterated`, transliteratedLetter)
  }
}

export const TranslatorApi = {
  translate (text, source, target) {
    return Api.post('translate', { text, source, target })
  }
}
